import { ref, type Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import {
  fetchFrappeDocCount,
  normalizeFrappeListResult,
  withFrappeLoading,
} from "@/services/frappe/list";
import { serializeFrappeFilters, type FrappeFilterClause } from "@/services/frappe/filters";
import { GetCashierRuntimeDefaults } from "@/api/cashierApi";
import { GetPosProfileDetail } from "@/api/posProfileApi";
import { Controllers, FrappeMethodType } from "@/type/api";
import type {
  ManualPaymentEntryPayload,
  PaymentEntryDetail,
  PaymentEntryFilterParam,
  PaymentEntryListItem,
  PaymentEntryListResult,
  PaymentEntryReference,
  PaymentEntryReferenceDoctype,
  PaymentEntryStatus,
  PaymentEntrySummary,
  PaymentEntryType,
  PaymentEntryUpdatePayload,
  PurchaseInvoicePaymentPayload,
  ReferencedPaymentEntryPayload,
  SalesInvoicePaymentPayload,
} from "@/type/paymentEntry";

const PAYMENT_ENTRY_DOCTYPE = "Payment Entry";
const PURCHASE_INVOICE_DOCTYPE = "Purchase Invoice";
const SALES_INVOICE_DOCTYPE = "Sales Invoice";
const GET_PAYMENT_ENTRY_METHOD = "erpnext.accounts.doctype.payment_entry.payment_entry.get_payment_entry";
const CREATE_PAYMENT_ENTRY_WITH_CASHIER_CONTEXT_METHOD = "pet_app.api.accounting.cashier.create_payment_entry_with_cashier_context";
const PAYMENT_ENTRY_PARTY_TYPE_FALLBACKS = ["Customer", "Supplier", "Employee", "Shareholder"];

const paymentEntryService = new HttpClient(Controllers.paymentEntry);

const PAYMENT_ENTRY_FIELDS = JSON.stringify([
  "name",
  "payment_type",
  "company",
  "posting_date",
  "party",
  "party_type",
  "paid_amount",
  "received_amount",
  "mode_of_payment",
  "reference_no",
  "reference_date",
  "remarks",
  "paid_from",
  "paid_to",
  "docstatus",
  "modified",
]);

interface PaymentEntryReferenceRow {
  doctype?: string;
  name?: string | null;
  reference_doctype?: string | null;
  reference_name?: string | null;
  total_amount?: number | string | null;
  outstanding_amount?: number | string | null;
  allocated_amount?: number | string | null;
  due_date?: string | null;
  bill_no?: string | null;
}

interface PaymentEntryDoc {
  doctype?: string;
  name?: string | null;
  payment_type?: string | null;
  company?: string | null;
  posting_date?: string | null;
  paid_amount?: number | string | null;
  received_amount?: number | string | null;
  party?: string | null;
  party_name?: string | null;
  party_type?: string | null;
  mode_of_payment?: string | null;
  reference_no?: string | null;
  reference_date?: string | null;
  remarks?: string | null;
  paid_from?: string | null;
  paid_to?: string | null;
  paid_from_account_currency?: string | null;
  paid_to_account_currency?: string | null;
  docstatus?: number | string | null;
  modified?: string | null;
  references?: PaymentEntryReferenceRow[] | null;
  [key: string]: any;
}

interface FrappeMetaField {
  fieldname?: string | null;
  options?: string | null;
}

interface FrappeMetaDoc {
  fields?: FrappeMetaField[] | null;
}

async function getFrappeMeta(doctype: string): Promise<FrappeMetaDoc | null> {
  const response = await paymentEntryService.get<any>(
    "frappe.desk.form.load.getdoctype",
    {},
    {
      doctype,
      with_parent: 1,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  const payload = response?.message ?? response?.data ?? response ?? null;
  const docs = Array.isArray(payload?.docs)
    ? payload.docs
    : Array.isArray(payload)
      ? payload
      : [];

  return docs.find((doc: any) => Array.isArray(doc?.fields))
    ?? payload?.meta
    ?? null;
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeString(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function mapDocstatus(value: unknown): PaymentEntryStatus {
  const numeric = Number(value ?? 0);
  if (numeric === 1)
    return "Submitted";
  if (numeric === 2)
    return "Cancelled";

  return "Draft";
}

function isDraftDoc(doc: { docstatus?: unknown } | null | undefined) {
  return Number(doc?.docstatus ?? 0) === 0;
}

function mapPaymentEntrySummary(doc: PaymentEntryDoc): PaymentEntrySummary {
  return {
    id: String(doc.name ?? ""),
    paymentType: doc.payment_type ? String(doc.payment_type) : null,
    company: doc.company ? String(doc.company) : null,
    postingDate: doc.posting_date ? String(doc.posting_date) : null,
    paidAmount: toNumber(doc.paid_amount),
    receivedAmount: toNumber(doc.received_amount),
    party: doc.party ? String(doc.party) : null,
    partyType: doc.party_type ? String(doc.party_type) : null,
    modeOfPayment: doc.mode_of_payment ? String(doc.mode_of_payment) : null,
    referenceNo: doc.reference_no ? String(doc.reference_no) : null,
    referenceDate: doc.reference_date ? String(doc.reference_date) : null,
    status: mapDocstatus(doc.docstatus),
  };
}

function mapPaymentEntryReference(row: PaymentEntryReferenceRow, index: number): PaymentEntryReference {
  return {
    id: String(row.name ?? `payment-entry-reference-${index}`),
    referenceDoctype: row.reference_doctype ? String(row.reference_doctype) : null,
    referenceName: row.reference_name ? String(row.reference_name) : null,
    totalAmount: toNumber(row.total_amount),
    outstandingAmount: toNumber(row.outstanding_amount),
    allocatedAmount: toNumber(row.allocated_amount),
    dueDate: row.due_date ? String(row.due_date) : null,
    billNo: row.bill_no ? String(row.bill_no) : null,
  };
}

function mapPaymentEntryListItem(doc: PaymentEntryDoc): PaymentEntryListItem {
  const references = (doc.references ?? []).map(mapPaymentEntryReference);
  const firstReference = references[0];

  return {
    ...mapPaymentEntrySummary(doc),
    paidFrom: doc.paid_from ? String(doc.paid_from) : null,
    paidTo: doc.paid_to ? String(doc.paid_to) : null,
    remarks: doc.remarks ? String(doc.remarks) : null,
    referenceName: firstReference?.referenceName ?? null,
    referenceDoctype: firstReference?.referenceDoctype ?? null,
  };
}

function mapPaymentEntryDetail(doc: PaymentEntryDoc): PaymentEntryDetail {
  return {
    ...mapPaymentEntryListItem(doc),
    modified: doc.modified ? String(doc.modified) : null,
    currency: normalizeString(doc.paid_from_account_currency ?? doc.paid_to_account_currency),
    partyName: normalizeString(doc.party_name),
    references: (doc.references ?? []).map(mapPaymentEntryReference),
  };
}

function normalizePaymentEntryPartyTypes(metaDoc: FrappeMetaDoc | null | undefined) {
  const rawOptions = metaDoc?.fields?.find(field => field.fieldname === "party_type")?.options;
  const parsed = String(rawOptions ?? "")
    .split("\n")
    .map(option => option.trim())
    .filter(Boolean);

  return parsed.length ? parsed : PAYMENT_ENTRY_PARTY_TYPE_FALLBACKS;
}

async function fetchPaymentEntryDoc(id: string) {
  const response = await paymentEntryService.get<any>(
    "frappe.client.get",
    {},
    {
      doctype: PAYMENT_ENTRY_DOCTYPE,
      name: id,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? null;
}

async function preparePaymentEntryDoc(payload: ReferencedPaymentEntryPayload): Promise<PaymentEntryDoc | null> {
  const normalizedPayload = normalizeReferencedPaymentPayload(payload);
  const response = await paymentEntryService.post<any>(
    {
      dt: normalizedPayload.referenceDoctype,
      dn: normalizedPayload.referenceName,
      party_amount: Number(normalizedPayload.amount ?? 0),
      payment_type: normalizedPayload.paymentType,
    },
    GET_PAYMENT_ENTRY_METHOD,
    {},
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? null;
}

function normalizeReferencedPaymentPayload(
  payload: ReferencedPaymentEntryPayload,
): ReferencedPaymentEntryPayload {
  if (payload.paymentType === "Receive") {
    return {
      ...payload,
      referenceDoctype: SALES_INVOICE_DOCTYPE,
    };
  }

  if (payload.paymentType === "Pay") {
    return {
      ...payload,
      referenceDoctype: PURCHASE_INVOICE_DOCTYPE,
    };
  }

  return payload;
}

async function submitReceivePaymentIfNeeded(
  doc: PaymentEntryDoc,
  paymentType: PaymentEntryType,
) {
  if (paymentType !== "Receive")
    return doc;

  return submitPaymentEntryDoc(doc);
}

function buildReferencedPaymentEntryDoc(
  preparedDoc: PaymentEntryDoc,
  payload: ReferencedPaymentEntryPayload,
) {
  const amount = toNumber(payload.amount);
  const existingReferences = Array.isArray(preparedDoc.references)
    ? preparedDoc.references
    : [];

  const references = existingReferences.length
    ? existingReferences.map((reference) => {
        const matchesCurrentReference
          = String(reference.reference_name ?? "").trim() === payload.referenceName
            && String(reference.reference_doctype ?? "").trim() === payload.referenceDoctype;

        return matchesCurrentReference
          ? {
              ...reference,
              allocated_amount: amount,
            }
          : reference;
      })
    : [
        {
          doctype: "Payment Entry Reference",
          reference_doctype: payload.referenceDoctype,
          reference_name: payload.referenceName,
          allocated_amount: amount,
        },
      ];

  return {
    ...preparedDoc,
    doctype: PAYMENT_ENTRY_DOCTYPE,
    payment_type: payload.paymentType,
    posting_date: payload.postingDate,
    paid_amount: amount,
    received_amount: amount,
    mode_of_payment: payload.modeOfPayment || undefined,
    pos_profile: payload.posProfile || undefined,
    reference_no: payload.referenceNo || undefined,
    reference_date: payload.referenceDate || undefined,
    remarks: payload.remarks || preparedDoc.remarks || undefined,
    references,
  };
}

function buildUpdatedPaymentEntryDoc(
  existingDoc: PaymentEntryDoc,
  payload: PaymentEntryUpdatePayload,
) {
  const amount = toNumber(payload.amount);
  const updatedReferences = Array.isArray(existingDoc.references)
    ? existingDoc.references.map((reference, index) => {
        if (index !== 0)
          return reference;

        return {
          ...reference,
          allocated_amount: amount,
        };
      })
    : existingDoc.references;

  return {
    ...existingDoc,
    doctype: PAYMENT_ENTRY_DOCTYPE,
    company: payload.company || existingDoc.company || undefined,
    party_type: payload.partyType || existingDoc.party_type || undefined,
    party: payload.party || existingDoc.party || undefined,
    paid_from: payload.paidFrom || existingDoc.paid_from || undefined,
    paid_to: payload.paidTo || existingDoc.paid_to || undefined,
    posting_date: payload.postingDate,
    paid_amount: amount,
    received_amount: amount,
    mode_of_payment: payload.modeOfPayment || undefined,
    pos_profile: payload.posProfile || undefined,
    reference_no: payload.referenceNo || undefined,
    reference_date: payload.referenceDate || undefined,
    remarks: payload.remarks || undefined,
    references: updatedReferences,
  };
}

function buildManualPaymentEntryDoc(payload: ManualPaymentEntryPayload) {
  const amount = toNumber(payload.amount);

  return {
    doctype: PAYMENT_ENTRY_DOCTYPE,
    payment_type: payload.paymentType,
    company: payload.company || undefined,
    party_type: payload.paymentType === "Internal Transfer" ? undefined : payload.partyType || undefined,
    party: payload.paymentType === "Internal Transfer" ? undefined : payload.party || undefined,
    posting_date: payload.postingDate,
    paid_from: payload.paidFrom || undefined,
    paid_to: payload.paidTo || undefined,
    paid_amount: amount,
    received_amount: amount,
    mode_of_payment: payload.modeOfPayment || undefined,
    pos_profile: payload.posProfile || undefined,
    reference_no: payload.referenceNo || undefined,
    reference_date: payload.referenceDate || undefined,
    remarks: payload.remarks || undefined,
  };
}

async function savePaymentEntryDoc(doc: PaymentEntryDoc) {
  const response = await paymentEntryService.post<any>(
    {
      doc: JSON.stringify(doc),
    },
    "frappe.client.save",
    {},
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? {};
}

async function submitPaymentEntryDoc(doc: PaymentEntryDoc) {
  const response = await paymentEntryService.post<any>(
    {
      doc: JSON.stringify(doc),
    },
    "frappe.client.submit",
    {},
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? {};
}

async function ensurePaymentEntrySubmitted(doc: PaymentEntryDoc) {
  if (!isDraftDoc(doc))
    return doc;

  const existingDoc = doc.name ? await fetchPaymentEntryDoc(String(doc.name)) : doc;

  if (!existingDoc)
    return doc;
  if (!isDraftDoc(existingDoc))
    return existingDoc;

  return submitPaymentEntryDoc(existingDoc);
}

async function createCashierContextPaymentEntry(
  payload: {
    referenceDoctype: PaymentEntryReferenceDoctype;
    referenceName: string;
    paymentType: PaymentEntryType;
    postingDate: string;
    amount: number;
    posProfile: string;
    cashAccount?: string | null;
    modeOfPayment?: string | null;
    referenceNo?: string | null;
    referenceDate?: string | null;
    remarks?: string | null;
  },
): Promise<PaymentEntryDoc | null> {
  const [runtimeDefaults, profileDetail] = await Promise.all([
    GetCashierRuntimeDefaults(ref(false), payload.posProfile).catch(() => null),
    GetPosProfileDetail(payload.posProfile).catch(() => null),
  ]);
  const cashierCashAccount = normalizeString(payload.cashAccount)
    || normalizeString(runtimeDefaults?.cashAccount)
    || normalizeString(profileDetail?.cashAccount);

  let response: any;

  try {
    response = await paymentEntryService.post<any>(
      {
        profile: payload.posProfile,
        profile_name: payload.posProfile,
        pos_profile: payload.posProfile,
        cashier_profile: payload.posProfile,
        payment_type: payload.paymentType,
        reference_doctype: payload.referenceDoctype,
        reference_name: payload.referenceName,
        posting_date: payload.postingDate,
        amount: Number(payload.amount ?? 0),
        cash_account: cashierCashAccount || undefined,
        cashier_cash_account: cashierCashAccount || undefined,
        custom_cash_account: cashierCashAccount || undefined,
        custom_cashier_cash_account: cashierCashAccount || undefined,
        paid_to: payload.paymentType === "Receive" ? cashierCashAccount || undefined : undefined,
        paid_from: payload.paymentType === "Pay" ? cashierCashAccount || undefined : undefined,
        mode_of_payment: payload.modeOfPayment || undefined,
        reference_no: payload.referenceNo || undefined,
        reference_date: payload.referenceDate || undefined,
        remarks: payload.remarks || undefined,
        submit: 1,
      },
      CREATE_PAYMENT_ENTRY_WITH_CASHIER_CONTEXT_METHOD,
      {},
      undefined,
      FrappeMethodType.method,
    );
  } catch (error: any) {
    const message = String(error?.message ?? "");
    const canFallbackWithCashierAccount = cashierCashAccount
      && /Cashier Cash Account is required/i.test(message);

    if (!canFallbackWithCashierAccount)
      throw error;

    const preparedDoc = await preparePaymentEntryDoc(payload);
    if (!preparedDoc)
      throw error;

    const savedDoc = await savePaymentEntryDoc({
      ...buildReferencedPaymentEntryDoc(preparedDoc, payload),
      pos_profile: payload.posProfile,
      custom_pos_profile: payload.posProfile,
      cash_account: cashierCashAccount,
      cashier_cash_account: cashierCashAccount,
      custom_cashier_cash_account: cashierCashAccount,
      paid_to: payload.paymentType === "Receive" ? cashierCashAccount : preparedDoc.paid_to,
      paid_from: payload.paymentType === "Pay" ? cashierCashAccount : preparedDoc.paid_from,
    });

    return submitPaymentEntryDoc(savedDoc);
  }

  const payloadData = response?.message ?? response?.data ?? response ?? null;

  if (!payloadData)
    return null;

  const paymentEntry = payloadData?.payment_entry ?? payloadData?.paymentEntry ?? payloadData;

  return paymentEntry ? (paymentEntry as PaymentEntryDoc) : null;
}

export async function GetPaymentEntries(
  loading: Ref<boolean>,
  filter: PaymentEntryFilterParam = {},
): Promise<PaymentEntryListResult | null> {
  return withFrappeLoading(loading, async () => {
    const filters: FrappeFilterClause[] = [
      ...(filter.filters ?? []),
      ...(filter.status
        ? [[PAYMENT_ENTRY_DOCTYPE, "docstatus", "=", filter.status === "Submitted" ? 1 : filter.status === "Cancelled" ? 2 : 0] as FrappeFilterClause]
        : []),
      ...(filter.paymentType
        ? [[PAYMENT_ENTRY_DOCTYPE, "payment_type", "=", filter.paymentType] as FrappeFilterClause]
        : []),
    ];

    const searchOrFilters: FrappeFilterClause[] = filter.search?.trim()
      ? [
          [PAYMENT_ENTRY_DOCTYPE, "name", "like", `%${filter.search.trim()}%`],
          [PAYMENT_ENTRY_DOCTYPE, "party", "like", `%${filter.search.trim()}%`],
          [PAYMENT_ENTRY_DOCTYPE, "reference_no", "like", `%${filter.search.trim()}%`],
          [PAYMENT_ENTRY_DOCTYPE, "remarks", "like", `%${filter.search.trim()}%`],
        ]
      : [];
    const orFilters: FrappeFilterClause[] = [
      ...(filter.orFilters ?? []),
      ...searchOrFilters,
    ];

    const serializedFilters = serializeFrappeFilters(filters);
    const serializedOrFilters = serializeFrappeFilters(orFilters);

    try {
      const [response, total] = await Promise.all([
        paymentEntryService.get<{ data?: PaymentEntryDoc[] }>(
          "",
          {
            limit_start: filter.limit_start ?? 0,
            limit_page_length: filter.limit_page_length ?? 10,
            order_by: filter.order_by ?? "modified desc",
            ...(serializedFilters ? { filters: serializedFilters } : {}),
            ...(serializedOrFilters ? { or_filters: serializedOrFilters } : {}),
          },
          {},
          PAYMENT_ENTRY_FIELDS,
        ),
        fetchFrappeDocCount(
          paymentEntryService,
          PAYMENT_ENTRY_DOCTYPE,
          serializedFilters,
          serializedOrFilters,
        ).catch(() => 0),
      ]);

      const normalized = normalizeFrappeListResult(response, total);

      return {
        data: normalized.data.map(mapPaymentEntryListItem),
        total: normalized.total,
      };
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function GetPaymentEntryDetail(
  loading: Ref<boolean>,
  paymentEntryId: string,
): Promise<PaymentEntryDetail | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const doc = await fetchPaymentEntryDoc(paymentEntryId);
      if (!doc)
        return null;

      return mapPaymentEntryDetail(doc);
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function GetPaymentEntryPartyTypes(
  loading: Ref<boolean>,
): Promise<string[]> {
  return withFrappeLoading(loading, async () => {
    try {
      const metaDoc = await getFrappeMeta(PAYMENT_ENTRY_DOCTYPE);
      return normalizePaymentEntryPartyTypes(metaDoc);
    } catch {
      return PAYMENT_ENTRY_PARTY_TYPE_FALLBACKS;
    }
  });
}

export async function CreateReferencedPaymentEntry(
  loading: Ref<boolean>,
  payload: ReferencedPaymentEntryPayload,
): Promise<PaymentEntryDetail | null> {
  return withFrappeLoading(loading, async () => {
    const normalizedPayload = normalizeReferencedPaymentPayload(payload);
    const preparedDoc = await preparePaymentEntryDoc(normalizedPayload);
    if (!preparedDoc)
      return null;

    const doc = buildReferencedPaymentEntryDoc(preparedDoc, normalizedPayload);
    const savedDoc = await savePaymentEntryDoc(doc);
    const finalDoc = await submitReceivePaymentIfNeeded(savedDoc, normalizedPayload.paymentType);

    return mapPaymentEntryDetail(finalDoc);
  });
}

export async function CreateManualPaymentEntry(
  loading: Ref<boolean>,
  payload: ManualPaymentEntryPayload,
): Promise<PaymentEntryDetail | null> {
  return withFrappeLoading(loading, async () => {
    const savedDoc = await savePaymentEntryDoc(buildManualPaymentEntryDoc(payload));
    const finalDoc = await submitReceivePaymentIfNeeded(savedDoc, payload.paymentType);

    return mapPaymentEntryDetail(finalDoc);
  });
}

export async function UpdatePaymentEntry(
  loading: Ref<boolean>,
  paymentEntryId: string,
  payload: PaymentEntryUpdatePayload,
): Promise<PaymentEntryDetail | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchPaymentEntryDoc(paymentEntryId);
    if (!existingDoc)
      return null;
    if (!isDraftDoc(existingDoc))
      throw new Error("Only draft payment entries can be edited.");

    const doc = buildUpdatedPaymentEntryDoc(existingDoc, payload);
    const savedDoc = await savePaymentEntryDoc(doc);

    return mapPaymentEntryDetail(savedDoc);
  });
}

export async function SubmitPaymentEntry(
  loading: Ref<boolean>,
  paymentEntryId: string,
): Promise<PaymentEntryDetail | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchPaymentEntryDoc(paymentEntryId);
    if (!existingDoc)
      return null;
    if (!isDraftDoc(existingDoc))
      throw new Error("Only draft payment entries can be submitted.");

    const submittedDoc = await submitPaymentEntryDoc(existingDoc);

    return mapPaymentEntryDetail(submittedDoc);
  });
}

/**
 * Reverses a submitted payment entry. The backend unwinds the GL entries and restores the
 * outstanding amount on every invoice this entry had allocated against.
 *
 * ⚠️ `frappe.client.cancel` takes doctype + name, NOT a `{ doc }` payload like
 * `frappe.client.submit` does. Sending `{ doc }` here silently cancels nothing.
 */
export async function CancelPaymentEntry(
  loading: Ref<boolean>,
  paymentEntryId: string,
): Promise<PaymentEntryDetail | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchPaymentEntryDoc(paymentEntryId);
    if (!existingDoc)
      return null;

    if (Number(existingDoc.docstatus ?? 0) !== 1)
      throw new Error("Only submitted payment entries can be cancelled.");

    const response = await paymentEntryService.post<any>(
      {
        doctype: PAYMENT_ENTRY_DOCTYPE,
        name: paymentEntryId,
      },
      "frappe.client.cancel",
      {},
      undefined,
      FrappeMethodType.method,
    );

    const cancelledDoc = response?.message ?? response?.data ?? response ?? {};

    // Re-read unless the response carried the reference rows: a detail mapped from a dict
    // without `references` would blank the allocation table on the detail page.
    if (Array.isArray(cancelledDoc?.references))
      return mapPaymentEntryDetail(cancelledDoc);

    const reread = await fetchPaymentEntryDoc(paymentEntryId);

    return reread ? mapPaymentEntryDetail(reread) : null;
  });
}

export async function CreatePurchaseInvoicePaymentEntry(
  loading: Ref<boolean>,
  payload: PurchaseInvoicePaymentPayload,
): Promise<PaymentEntrySummary | null> {
  return withFrappeLoading(loading, async () => {
    const preparedDoc = await preparePaymentEntryDoc({
      referenceDoctype: PURCHASE_INVOICE_DOCTYPE,
      referenceName: payload.purchaseInvoiceId,
      paymentType: payload.paymentType,
      postingDate: payload.postingDate,
      amount: payload.amount,
      posProfile: payload.posProfile,
      modeOfPayment: payload.modeOfPayment,
      referenceNo: payload.referenceNo,
      referenceDate: payload.referenceDate,
      remarks: payload.remarks,
    });

    if (!preparedDoc)
      return null;

    const savedDoc = await savePaymentEntryDoc(
      buildReferencedPaymentEntryDoc(preparedDoc, {
        referenceDoctype: PURCHASE_INVOICE_DOCTYPE,
        referenceName: payload.purchaseInvoiceId,
        paymentType: payload.paymentType,
        postingDate: payload.postingDate,
        amount: payload.amount,
        posProfile: payload.posProfile,
        cashAccount: payload.cashAccount,
        modeOfPayment: payload.modeOfPayment,
        referenceNo: payload.referenceNo,
        referenceDate: payload.referenceDate,
        remarks: payload.remarks,
      }),
    );

    const submittedDoc = await submitPaymentEntryDoc(savedDoc);

    return mapPaymentEntrySummary(submittedDoc);
  });
}

export async function CreateSalesInvoicePaymentEntry(
  loading: Ref<boolean>,
  payload: SalesInvoicePaymentPayload,
): Promise<PaymentEntrySummary | null> {
  return withFrappeLoading(loading, async () => {
    if (payload.posProfile) {
      const cashierPaymentDoc = await createCashierContextPaymentEntry({
        referenceDoctype: SALES_INVOICE_DOCTYPE,
        referenceName: payload.salesInvoiceId,
        paymentType: payload.paymentType,
        postingDate: payload.postingDate,
        amount: payload.amount,
        posProfile: payload.posProfile,
        cashAccount: payload.cashAccount,
        modeOfPayment: payload.modeOfPayment,
        referenceNo: payload.referenceNo,
        referenceDate: payload.referenceDate,
        remarks: payload.remarks,
      });

      if (!cashierPaymentDoc)
        return null;

      const submittedPaymentDoc = await ensurePaymentEntrySubmitted(cashierPaymentDoc);

      return mapPaymentEntrySummary(submittedPaymentDoc);
    }

    const preparedDoc = await preparePaymentEntryDoc({
      referenceDoctype: SALES_INVOICE_DOCTYPE,
      referenceName: payload.salesInvoiceId,
      paymentType: payload.paymentType,
      postingDate: payload.postingDate,
      amount: payload.amount,
      posProfile: payload.posProfile,
      modeOfPayment: payload.modeOfPayment,
      referenceNo: payload.referenceNo,
      referenceDate: payload.referenceDate,
      remarks: payload.remarks,
    });

    if (!preparedDoc)
      return null;

    const savedDoc = await savePaymentEntryDoc(
      buildReferencedPaymentEntryDoc(preparedDoc, {
        referenceDoctype: SALES_INVOICE_DOCTYPE,
        referenceName: payload.salesInvoiceId,
        paymentType: payload.paymentType,
        postingDate: payload.postingDate,
        amount: payload.amount,
        posProfile: payload.posProfile,
        modeOfPayment: payload.modeOfPayment,
        referenceNo: payload.referenceNo,
        referenceDate: payload.referenceDate,
        remarks: payload.remarks,
      }),
    );

    const submittedDoc = await submitPaymentEntryDoc(savedDoc);

    return mapPaymentEntrySummary(submittedDoc);
  });
}
